'use client'

import * as React from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useTaxExemptionsList } from '@/lib/api/taxExemptions'
import { TaxExemptionsTable } from './components/TaxExemptionsTable'
import { MainTableState } from '@/lib/utils/table-state-utils'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { Card, CardContent } from '@/components/ui/card'

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(TaxExemptionsPage, PERMISSIONS.STORES_VIEW);

function TaxExemptionsPage() {
	const { t } = useTranslation('taxes')
	const { id, storeRoutes } = useStoreNavigate()

	const [tableState, setTableState] = React.useState<MainTableState>({
		pagination: { pageIndex: 0, pageSize: 20 },
		sorting: [{ id: 'createdAt', desc: true }],
		columnFilters: [],
	})

	const {
		data: response,
		isLoading: loading,
		error,
		refetch,
	} = useTaxExemptionsList({
		storeId: id,
		pageNumber: tableState.pagination.pageIndex + 1,
		pageSize: tableState.pagination.pageSize,
	})

	const items = response?.items || []
	const total = response?.total || 0

	return (
		<div className="flex flex-col space-y-4 sm:space-y-6">
			{/* Page header */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
				<div className="flex items-center gap-4">
					<BackButton to={storeRoutes.TAXES()} />
					<div>
						<h1 className="text-xl font-semibold sm:text-2xl">{t('exemptions.title')}</h1>
						<p className="text-muted-foreground text-sm">{t('exemptions.description')}</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Link to={storeRoutes.TAX_EXEMPTIONS_CREATE()}>
						<Button className="w-full sm:w-auto">
							<Plus className="mr-2 h-4 w-4" />
							{t('exemptions.addExemptions')}
						</Button>
					</Link>
				</div>
			</div>

			{/* Tax exemptions table */}
			<Card>
				<CardContent>
					<TaxExemptionsTable
						items={items}
						total={total}
						loading={loading}
						onTableStateChange={setTableState}
						onRefetch={refetch}
					/>
				</CardContent>
			</Card>
			{/* Error message */}
			{error && (
				<div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
					{error instanceof Error ? error.message : t('exemptions.loadError')}
				</div>
			)}
		</div>
	)
}
